import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Bell, Mail, MessageSquare, Send, CheckCircle, Plus, X, Loader2 } from 'lucide-react';

export default function NotificationsPage() {
  const [settings, setSettings] = useState({
    email_recipients: [], sms_recipients: [],
    weekly_email_enabled: true, sms_alerts_enabled: false,
    send_day: 'monday', send_hour: 8
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ email: false, sms: false, weekly: false });
  const [emailInput, setEmailInput] = useState('');
  const [smsInput, setSmsInput] = useState('');

  useEffect(() => {
    axios.get(`${API}/notifications/settings`)
      .then(r => { if (r.data && Object.keys(r.data).length) setSettings(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const addEmail = () => {
    const v = emailInput.trim().toLowerCase();
    if (!v || !v.includes('@')) { toast.error('Valid email required'); return; }
    if (settings.email_recipients.includes(v)) return;
    setSettings(s => ({ ...s, email_recipients: [...s.email_recipients, v] }));
    setEmailInput('');
  };

  const addSms = () => {
    const v = smsInput.trim();
    if (!v) return;
    setSettings(s => ({ ...s, sms_recipients: [...s.sms_recipients, v] }));
    setSmsInput('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notifications/settings`, settings);
      toast.success('Notification settings saved!');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const testEmail = async (to) => {
    setTesting(t => ({ ...t, email: true }));
    try {
      const res = await axios.post(`${API}/notifications/test-email`, { to });
      if (res.data.success) toast.success(`Test email sent to ${to}`);
      else toast.error('Failed to send — check Mailgun configuration');
    } catch { toast.error('Failed to send test email'); }
    finally { setTesting(t => ({ ...t, email: false })); }
  };

  const testSms = async (to) => {
    setTesting(t => ({ ...t, sms: true }));
    try {
      const res = await axios.post(`${API}/notifications/test-sms`, { to });
      if (res.data.success) toast.success(`Test SMS sent to ${to}`);
      else toast.error('Failed to send — check Twilio configuration');
    } catch { toast.error('Failed to send test SMS'); }
    finally { setTesting(t => ({ ...t, sms: false })); }
  };

  const sendWeeklySummary = async () => {
    setTesting(t => ({ ...t, weekly: true }));
    try {
      const res = await axios.post(`${API}/notifications/send-weekly-summary`);
      toast.success(res.data.message || 'Weekly summary sent!');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setTesting(t => ({ ...t, weekly: false })); }
  };

  if (loading) return <div className="text-slate-400 text-sm p-4">Loading...</div>;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="font-heading text-xl font-bold text-white tracking-tight flex items-center gap-2">
          <Bell size={20} className="text-blue-400" /> Notifications
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          Weekly pacing reports and performance alerts via email and SMS.
        </p>
      </div>

      {/* Email Recipients */}
      <Card style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)' }}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-heading text-white flex items-center gap-2">
              <Mail size={15} className="text-blue-400" /> Email Reports
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Weekly summary</span>
              <Switch checked={settings.weekly_email_enabled}
                onCheckedChange={v => setSettings(s => ({ ...s, weekly_email_enabled: v }))} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input value={emailInput} onChange={e => setEmailInput(e.target.value)}
              placeholder="Add email address..." type="email" className="h-9"
              onKeyDown={e => { if (e.key === 'Enter') addEmail(); }} />
            <Button size="sm" variant="outline" className="h-9" onClick={addEmail}><Plus size={14} /></Button>
          </div>
          <div className="space-y-1.5">
            {settings.email_recipients.map(email => (
              <div key={email} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-700">{email}</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 hover:text-blue-600"
                    onClick={() => testEmail(email)} disabled={testing.email}>
                    {testing.email ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Test
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-red-500"
                    onClick={() => setSettings(s => ({ ...s, email_recipients: s.email_recipients.filter(e => e !== email) }))}>
                    <X size={13} />
                  </Button>
                </div>
              </div>
            ))}
            {!settings.email_recipients.length && (
              <p className="text-xs text-slate-400 py-2">No email recipients added yet</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* SMS Recipients */}
      <Card style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)' }}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-heading text-white flex items-center gap-2">
              <MessageSquare size={15} className="text-blue-400" /> SMS Alerts
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Enable SMS</span>
              <Switch checked={settings.sms_alerts_enabled}
                onCheckedChange={v => setSettings(s => ({ ...s, sms_alerts_enabled: v }))} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input value={smsInput} onChange={e => setSmsInput(e.target.value)}
              placeholder="+1 (555) 000-0000" type="tel" className="h-9"
              onKeyDown={e => { if (e.key === 'Enter') addSms(); }} />
            <Button size="sm" variant="outline" className="h-9" onClick={addSms}><Plus size={14} /></Button>
          </div>
          <div className="space-y-1.5">
            {settings.sms_recipients.map(phone => (
              <div key={phone} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-700">{phone}</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 hover:text-blue-600"
                    onClick={() => testSms(phone)} disabled={testing.sms}>
                    {testing.sms ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Test
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-red-500"
                    onClick={() => setSettings(s => ({ ...s, sms_recipients: s.sms_recipients.filter(p => p !== phone) }))}>
                    <X size={13} />
                  </Button>
                </div>
              </div>
            ))}
            {!settings.sms_recipients.length && (
              <p className="text-xs text-slate-400 py-2">No SMS recipients added yet</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Weekly summary preview */}
      <Card style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)' }}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading text-white">Weekly Summary</CardTitle>
          <p className="text-xs text-slate-400 mt-0.5">
            Sent every Monday morning — location KPIs, pacing vs monthly goals, close rates, collections.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 text-xs text-blue-700 space-y-1">
            <p className="font-semibold">📊 What's included in the weekly email:</p>
            <p>• FA Presented, FA Sold, Close Rate per location</p>
            <p>• Arches sold this week vs 7-day pro-rated goal</p>
            <p>• Collections total for the week</p>
            <p>• Pacing indicator (on track / behind) per location</p>
          </div>
          <Button onClick={sendWeeklySummary} variant="outline" className="gap-2" disabled={testing.weekly}>
            {testing.weekly ? <><Loader2 size={14} className="animate-spin" /> Sending...</> : <><Send size={14} /> Send Weekly Summary Now</>}
          </Button>
          <p className="text-xs text-slate-400">Use this to test the report before it auto-sends.</p>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full h-10 font-semibold gap-2">
        <Save size={14} /> {saving ? 'Saving...' : 'Save Notification Settings'}
      </Button>
    </div>
  );
}
